// ─── Catálogo de permisos de mezo ────────────────────────────────────────────
//
// Pedido del cliente piloto (Tres Orquídeas): "que se divida por roles de
// cajero, administrador, mesero, cocinero y que cada uno tenga permisos
// diferentes. Que el mesero solo pueda registrar las mesas, no eliminar
// productos. Que el cajero pueda solo cobrar y no eliminar. Que el
// administrador tenga todos los permisos."
//
// Este archivo es la FUENTE DE VERDAD del lado del frontend. Tiene un espejo
// en SQL (`mezo_permisos_rol` en supabase/migrations/0049_roles_permisos.sql)
// que aplica RLS de verdad sobre lo destructivo. Si cambias un default aquí,
// cámbialo también allá — el frontend solo oculta botones; la DB es la que
// realmente bloquea.
//
// Modelo (3 capas, cada una pisa la anterior):
//   1. Default de mezo por rol (DEFAULTS_POR_ROL / mezo_permisos_rol en SQL).
//   2. Override del NEGOCIO por rol (businesses.permisos_roles, 0052): lo que
//      el administrador decide que puede cada rol en SU negocio — aplica a
//      todos los empleados con ese rol. Forma: { CASHIER: { 'pos.anular_linea': true } }
//      (solo diferencias con el default). ADMIN no se edita.
//   3. Override por persona (team_members.permisos, 0049): { permiso: true|false }
//      que el administrador ajusta al editar al empleado.
// permisosEfectivos(rol, overridesEmpleado, overridesRolNegocio) aplica las
// tres en ese orden. ADMIN siempre tiene todo (no se puede recortar).

// Orden de los grupos = orden en que se pintan en la matriz de /empleados.
export const GRUPOS_PERMISOS = [
  { id: 'pos',           label: 'POS / Caja' },
  { id: 'mesas',         label: 'Mesas' },
  { id: 'cocina',        label: 'Cocina' },
  { id: 'productos',     label: 'Productos' },
  { id: 'inventario',    label: 'Inventario' },
  { id: 'ventas',        label: 'Ventas' },
  { id: 'clientes',      label: 'Clientes' },
  { id: 'asistencia',    label: 'Asistencia' },
  { id: 'administracion', label: 'Administración' },
];

export const PERMISOS = [
  // POS / Caja
  { id: 'pos.vender',          grupo: 'pos',  label: 'Tomar pedidos y vender',        desc: 'Armar el carrito y enviar pedidos a cocina.' },
  { id: 'pos.cobrar',          grupo: 'pos',  label: 'Cobrar',                        desc: 'Confirmar pagos y cerrar la venta.' },
  { id: 'pos.editar_precio',   grupo: 'pos',  label: 'Editar precio por línea',       desc: 'Ajustar el precio de un producto al tomar la orden.' },
  { id: 'pos.descuentos',      grupo: 'pos',  label: 'Aplicar descuentos',            desc: 'Descuentos y canjes de fidelidad al cobrar.' },
  { id: 'pos.anular_linea',    grupo: 'pos',  label: 'Quitar productos / vaciar',     desc: 'Quitar ítems ya enviados a la mesa o vaciar el carrito.' },
  { id: 'pos.autorizar_descuentos', grupo: 'pos', label: 'Autorizar descuentos',      desc: 'Su PIN aprueba los descuentos que superan el tope del negocio (0067).' },
  // Mesas
  { id: 'mesas.ver',           grupo: 'mesas', label: 'Ver mesas',                    desc: 'Entrar a /mesas y ver el estado del salón.' },
  { id: 'mesas.gestionar',     grupo: 'mesas', label: 'Gestionar mesas y zonas',      desc: 'Crear mesas, mover, organizar el plano, crear/editar zonas.' },
  { id: 'mesas.eliminar',      grupo: 'mesas', label: 'Eliminar mesas y zonas',       desc: 'Borrar mesas o zonas del salón.' },
  // Cocina
  { id: 'cocina.ver',          grupo: 'cocina', label: 'Pantalla de cocina',          desc: 'Ver y avanzar comandas en /cocina.' },
  // Productos
  { id: 'productos.ver',       grupo: 'productos', label: 'Ver productos',            desc: 'Entrar al catálogo y ver fichas.' },
  { id: 'productos.editar',    grupo: 'productos', label: 'Crear y editar productos', desc: 'Crear, editar, importar productos y categorías.' },
  { id: 'productos.eliminar',  grupo: 'productos', label: 'Eliminar productos',       desc: 'Borrar productos y categorías.' },
  // Inventario
  { id: 'inventario.ver',      grupo: 'inventario', label: 'Ver inventario',          desc: 'Consultar stock y alertas.' },
  { id: 'inventario.editar',   grupo: 'inventario', label: 'Editar inventario',       desc: 'Agregar, reponer, ajustar, importar o eliminar ítems.' },
  // Ventas
  { id: 'ventas.ver',          grupo: 'ventas', label: 'Historial de ventas',         desc: 'Ver /ventas y reimprimir tickets.' },
  { id: 'ventas.anular',       grupo: 'ventas', label: 'Anular ventas',               desc: 'Anular una venta cerrada.' },
  { id: 'arqueo.ver',          grupo: 'ventas', label: 'Arqueo de caja',              desc: 'Ver el cuadre de caja.' },
  { id: 'caja.abrir_cerrar',   grupo: 'ventas', label: 'Abrir y cerrar el día',       desc: 'Empezar día con base y cerrar caja.' },
  { id: 'reportes.ver',        grupo: 'ventas', label: 'Reportes',                    desc: 'Ver /reportes (ventas, márgenes, empleados).' },
  { id: 'propinas.repartir',   grupo: 'ventas', label: 'Repartir propinas',           desc: 'Ver lo recaudado y liquidar el reparto entre el equipo (0067).' },
  // Clientes
  { id: 'clientes.ver',        grupo: 'clientes', label: 'Ver clientes',              desc: 'Buscar clientes y ver sus puntos.' },
  { id: 'clientes.editar',     grupo: 'clientes', label: 'Editar clientes',           desc: 'Registrar visitas, canjes y recompensas.' },
  // Asistencia
  { id: 'asistencia.checklist', grupo: 'asistencia', label: 'Checklist de turnos', desc: 'Ver y enviar las tareas de apertura y cierre.' },
  // Administración
  { id: 'promociones.gestionar',   grupo: 'administracion', label: 'Promociones',        desc: 'Crear y activar promociones / happy hour.' },
  { id: 'menuqr.gestionar',        grupo: 'administracion', label: 'Menú QR',            desc: 'Editar el menú público y el autopedido.' },
  { id: 'empleados.gestionar',     grupo: 'administracion', label: 'Empleados',          desc: 'Crear empleados, roles y permisos.' },
  { id: 'configuracion.gestionar', grupo: 'administracion', label: 'Configuración',      desc: 'Datos del negocio, impuestos, apariencia.' },
  { id: 'planes.ver',              grupo: 'administracion', label: 'Planes y pagos',     desc: 'Ver el plan del negocio y pagar.' },
  { id: 'ia.usar',                 grupo: 'administracion', label: 'mezoIA',             desc: 'Usar el asistente de IA.' },
];

export const IDS_PERMISOS = PERMISOS.map((p) => p.id);

// Roles tal como viven en la DB (enum team_role). Los ids "front" son los que
// usan FormEmpleado/TarjetaEmpleado ('admin' | 'cajero' | 'mesero' | 'cocina').
// 'cajera' es el id histórico que devuelve api.js (roleMap) — se acepta igual.
export const ROLES = {
  ADMIN:   { id: 'ADMIN',   front: 'admin',  label: 'Administrador', color: '#C8903F', desc: 'Todo: vende, cobra, configura, maneja equipo, planes y reportes.' },
  CASHIER: { id: 'CASHIER', front: 'cajero', label: 'Cajero',        color: '#3DAA68', desc: 'Vende y cobra, maneja mesas y caja. No elimina ni configura.' },
  WAITER:  { id: 'WAITER',  front: 'mesero', label: 'Mesero',        color: '#6B9ED4', desc: 'Registra mesas y manda pedidos a cocina. No cobra ni elimina.' },
  KITCHEN: { id: 'KITCHEN', front: 'cocina', label: 'Cocina',        color: '#D9A437', desc: 'Solo la pantalla de cocina, productos e inventario en lectura.' },
};

export const ROL_FRONT_A_DB = { admin: 'ADMIN', cajero: 'CASHIER', cajera: 'CASHIER', mesero: 'WAITER', cocina: 'KITCHEN' };
export const ROL_DB_A_FRONT = { ADMIN: 'admin', CASHIER: 'cajero', WAITER: 'mesero', KITCHEN: 'cocina' };

// Acepta 'ADMIN', 'admin', 'cajera', 'Mesero'… y devuelve el id de DB o null.
export function normalizarRol(rol) {
  if (!rol) return null;
  const s = String(rol).trim();
  if (ROLES[s.toUpperCase()]) return s.toUpperCase();
  return ROL_FRONT_A_DB[s.toLowerCase()] ?? null;
}

// ─── Defaults por rol ────────────────────────────────────────────────────────
// Solo se listan los permisos en TRUE; el resto queda en false. ESPEJO de
// mezo_permisos_rol() en 0049 — mantener sincronizado.
const TRUE_POR_ROL = {
  ADMIN:   IDS_PERMISOS,
  CASHIER: [
    'pos.vender', 'pos.cobrar', 'pos.editar_precio', 'pos.descuentos',
    'mesas.ver', 'mesas.gestionar',
    'cocina.ver',
    'productos.ver',
    'ventas.ver', 'arqueo.ver', 'caja.abrir_cerrar',
    'asistencia.checklist',
    'clientes.ver', 'clientes.editar',
  ],
  WAITER: [
    'pos.vender', 'pos.descuentos',
    'mesas.ver', 'mesas.gestionar',
    'cocina.ver',
    'productos.ver',
    'asistencia.checklist',
    'clientes.ver',
  ],
  KITCHEN: [
    'cocina.ver',
    'productos.ver',
    'asistencia.checklist',
    'inventario.ver',
  ],
};

function _mapa(idsEnTrue) {
  const set = new Set(idsEnTrue);
  const out = {};
  for (const id of IDS_PERMISOS) out[id] = set.has(id);
  return out;
}

export const DEFAULTS_POR_ROL = {
  ADMIN:   _mapa(TRUE_POR_ROL.ADMIN),
  CASHIER: _mapa(TRUE_POR_ROL.CASHIER),
  WAITER:  _mapa(TRUE_POR_ROL.WAITER),
  KITCHEN: _mapa(TRUE_POR_ROL.KITCHEN),
};

export const TODOS_LOS_PERMISOS = DEFAULTS_POR_ROL.ADMIN;

// Aplica un mapa de overrides {permiso: true|false} sobre `base` (copia).
// Solo se respetan valores booleanos de permisos del catálogo; el resto se
// ignora (así un jsonb con basura nunca abre ni cierra nada por accidente).
function _aplicarOverrides(base, overrides) {
  const out = { ...base };
  if (overrides && typeof overrides === 'object') {
    for (const [k, v] of Object.entries(overrides)) {
      if (typeof v === 'boolean' && k in out) out[k] = v;
    }
  }
  return out;
}

// Permisos del ROL en un negocio = default de mezo + override del negocio
// para ese rol (capa 1 + capa 2). Es la "base" que ve la matriz por empleado
// y la pestaña "Roles y permisos". ADMIN → todo; rol desconocido → nada.
// `overridesRolNegocio` es SOLO el mapa de ese rol ({ 'pos.cobrar': true }),
// no el objeto completo por roles.
export function permisosRolNegocio(role, overridesRolNegocio = null) {
  const rol = normalizarRol(role);
  if (rol === 'ADMIN') return { ...TODOS_LOS_PERMISOS };
  const base = DEFAULTS_POR_ROL[rol] ?? _mapa([]);
  return _aplicarOverrides(base, overridesRolNegocio);
}

// Permisos efectivos de una persona:
//   default del rol → override del negocio para el rol → override del empleado.
// - ADMIN ignora TODO (nunca se le recorta nada: es el dueño del negocio).
// - Un rol desconocido/null NO da nada: quien llama decide si lo trata como
//   ADMIN (usePermisos lo hace para cuentas dueñas viejas sin team_member).
// - Compatible con la firma vieja de 2 args (sin capa 2 → solo defaults).
export function permisosEfectivos(role, overrides = {}, overridesRolNegocio = null) {
  const rol = normalizarRol(role);
  if (rol === 'ADMIN') return { ...TODOS_LOS_PERMISOS };
  return _aplicarOverrides(permisosRolNegocio(rol, overridesRolNegocio), overrides);
}

// ─── Rutas del panel → permiso requerido ─────────────────────────────────────
// null = cualquier miembro del negocio. Lo usan ProtectedRoute (redirect) y el
// sidebar (DashboardLayout) para ocultar secciones. Se compara por prefijo más
// largo, así '/pos?mesaId=…' resuelve a '/pos'.
export const PERMISO_POR_RUTA = {
  '/dashboard':     null,
  '/inicio':        null,
  '/mensajes':      null,
  '/mi-turno':      null,   // cada empleado marca SU entrada/salida (asistencia 0050)
  '/pos':           'pos.vender',
  '/mesas':         'mesas.ver',
  '/cocina':        'cocina.ver',
  '/ventas':        'ventas.ver',
  '/arqueo':        'arqueo.ver',
  '/reportes':      'reportes.ver',
  '/propinas':      'propinas.repartir',
  '/productos':     'productos.ver',
  '/inventario':    'inventario.ver',
  '/clientes':      'clientes.ver',
  '/promociones':   'promociones.gestionar',
  '/menu-qr':       'menuqr.gestionar',
  '/empleados':     'empleados.gestionar',
  '/configuracion': 'configuracion.gestionar',
  '/planes':        'planes.ver',
  '/mezo-ia':       'ia.usar',
};

// ¿Con este mapa de permisos efectivos se puede entrar a `path`?
// Rutas fuera del mapa (landing, legales, /admin-mezo…) → true: el gate de
// permisos solo aplica a las secciones del panel.
export function rutaPermitida(permisos, path) {
  if (!path) return true;
  const limpio = String(path).split('?')[0].split('#')[0];
  let mejor = null;
  for (const ruta of Object.keys(PERMISO_POR_RUTA)) {
    const coincide = limpio === ruta || limpio.startsWith(ruta + '/');
    if (coincide && (mejor === null || ruta.length > mejor.length)) mejor = ruta;
  }
  if (mejor === null) return true;
  const permiso = PERMISO_POR_RUTA[mejor];
  if (!permiso) return true;
  return permisos?.[permiso] === true;
}

// Diferencias entre lo efectivo y la base del rol — lo que realmente hay
// que persistir como override (así cambiar de rol no arrastra basura).
// `overridesRolNegocio` (capa 2) hace que la base sea el rol DEL NEGOCIO:
// un empleado solo guarda lo que difiere de lo que ya tiene su rol ahí.
export function overridesDesdeEfectivos(role, efectivos, overridesRolNegocio = null) {
  const base = permisosRolNegocio(role, overridesRolNegocio);
  const out  = {};
  for (const id of IDS_PERMISOS) {
    const v = efectivos?.[id];
    if (typeof v === 'boolean' && v !== base[id]) out[id] = v;
  }
  return out;
}

// ─── Overrides por rol del negocio (businesses.permisos_roles, 0052) ─────────
// Forma: { CASHIER: { 'pos.anular_linea': true }, WAITER: { 'pos.cobrar': true } }
// Solo diferencias con el default de mezo; ADMIN nunca aparece.
export const ROLES_EDITABLES = ['CASHIER', 'WAITER', 'KITCHEN'];

// Mapa { rol: permisosCompletos } del negocio: lo que puede cada rol ahí
// (capa 1 + 2). Lo usa la pestaña "Roles y permisos" como estado editable.
export function permisosRolesNegocio(permisosRoles = {}) {
  const out = {};
  for (const rol of Object.keys(ROLES)) out[rol] = permisosRolNegocio(rol, permisosRoles?.[rol]);
  return out;
}

// Inverso: de { rol: permisosCompletos } a lo que se persiste (solo las
// diferencias con el default de mezo, sin ADMIN, sin roles vacíos). Es lo que
// viaja a set_permisos_roles.
export function overridesRolesDesdeMapa(mapaPorRol) {
  const out = {};
  for (const rol of ROLES_EDITABLES) {
    const diff = {};
    const efectivos = mapaPorRol?.[rol];
    const base = DEFAULTS_POR_ROL[rol];
    for (const id of IDS_PERMISOS) {
      const v = efectivos?.[id];
      if (typeof v === 'boolean' && v !== base[id]) diff[id] = v;
    }
    if (Object.keys(diff).length) out[rol] = diff;
  }
  return out;
}

// Limpia un permisos_roles que viene de afuera (RPC / mock / localStorage):
// solo roles editables, solo permisos del catálogo, solo booleanos distintos
// del default. Devuelve {} si no hay nada válido.
export function sanearPermisosRoles(crudo) {
  if (!crudo || typeof crudo !== 'object') return {};
  const mapa = {};
  for (const [rolCrudo, ov] of Object.entries(crudo)) {
    const rol = normalizarRol(rolCrudo);
    if (!rol || !ROLES_EDITABLES.includes(rol)) continue;
    mapa[rol] = permisosRolNegocio(rol, ov);
  }
  return overridesRolesDesdeMapa(mapa);
}

// ¿El rol tiene algún permiso distinto del default de mezo en este negocio?
export function rolPersonalizado(permisosRoles, rol) {
  const r = normalizarRol(rol);
  if (!r || r === 'ADMIN') return false;
  return Object.keys(sanearPermisosRoles({ [r]: permisosRoles?.[r] })[r] ?? {}).length > 0;
}
